import React, { useEffect } from 'react';
import { EmailNotificationView, EmailNotificationViewProps } from './EmailNotification.view';
import { ApolloError } from '@apollo/client';
import {
  CombinedOrganizationSettingsInput,
  GetEmailNotificationSettingsLazyQueryHookResult,
  UpdateEmailNotificationSettingsMutationFn,
  UpdateUserSettingsMutationFn,
  UserSettingsInput
} from '../../../models/graphql';
import { RegisterOptions, useForm } from 'react-hook-form';
import { FormOptions } from '../../../constants/form';
import { useSelectedOrganization } from '../../../hooks/useSelectedOrganization';
import { ToggleAction } from '../../../components/forms/Toggle/Toggle.controller';
import { useUserData } from '../../../hooks/useUserData';
import { useUserRole } from '../../../hooks/useUserRole';
import { remapApolloErrors } from '../../../helpers/remapApolloErrors';
import { showToast } from '../../../helpers/showToast';

export type EmailNotificationControllerProps = Omit<
  EmailNotificationViewProps,
  | 'rules'
  | 'toggleRules'
  | 'formInstance'
  | 'isLoading'
  | 'notifySubscriptionEnabledHandler'
  | 'notifyAutoRssEnabledHandler'
  | 'notifyQueueEmptyEnabledHandler'
  | 'notifyPostProfileErrorEnabledHandler'
  | 'notifyWeeklyStatsEnabledHandler'
  | 'notifyInstagramPostEnabledHandler'
  | 'notifyProductUpdatesEnabledHandler'
  | 'notifyMailingListEnabledHandler'
  | 'notifyReferralCreditsEnabledHandler'
  | 'notifyFeatureAddedEnabledHandler'
  | 'userSettings'
  | 'selectedOrgRole'
> & {
  getEmailNotificationSettings: GetEmailNotificationSettingsLazyQueryHookResult[0];
  updateEmailNotification: UpdateEmailNotificationSettingsMutationFn;
  updateUserSettings: UpdateUserSettingsMutationFn;
};

export const EmailNotificationController = (props: EmailNotificationControllerProps): JSX.Element => {
  const { userData } = useUserData();
  const userSettings = userData?.settings;
  const { selectedOrgRole } = useUserRole();
  const { selectedOrganization } = useSelectedOrganization();
  const rules: RegisterOptions = {};
  const toggleRules: RegisterOptions = {};

  const formInstance = useForm(FormOptions);

  useEffect(() => {
    selectedOrganization &&
      props.getEmailNotificationSettings({
        variables: { organizationId: selectedOrganization.id }
      });
    // TODO-FIX-TS-WARNINGS
    // eslint-disable-next-line
  }, [selectedOrganization]);

  const notifySubscriptionEnabledHandler: ToggleAction = (value, complete) => {
    if (selectedOrganization) {
      const input: CombinedOrganizationSettingsInput = {
        notifySubscriptionEnabled: value
      };

      props
        .updateEmailNotification({
          // variables: { input: input, organizationId: selectedOrganization.id }
          variables: { input: input, organizationId: selectedOrganization.id }
          // variables: { input: input }
        })
        .then(() => {
          complete(true);
        })
        .catch(e => {
          const errorForRemapping = e as ApolloError;
          const mappedError = remapApolloErrors(errorForRemapping);
          showToast({
            summary: 'Error in updating your settings',
            detail: mappedError,
            severity: 'error'
          });
          complete(false);
        });
    } else {
      complete(false);
    }
  };

  const notifyQueueEmptyEnabledHandler: ToggleAction = (value, complete) => {
    if (selectedOrganization) {
      const input: CombinedOrganizationSettingsInput = {
        notifyQueueEmptyEnabled: value
      };

      props
        .updateEmailNotification({
          // variables: { input: input, organizationId: selectedOrganization.id }
          variables: { input: input, organizationId: selectedOrganization.id }
          // variables: { input: input }
        })
        .then(() => {
          complete(true);
        })
        .catch(e => {
          const errorForRemapping = e as ApolloError;
          const mappedError = remapApolloErrors(errorForRemapping);
          showToast({
            summary: 'Error in updating your settings',
            detail: mappedError,
            severity: 'error'
          });
          complete(false);
        });
    } else {
      complete(false);
    }
  };

  const notifyWeeklyStatsEnabledHandler: ToggleAction = (value, complete) => {
    if (selectedOrganization) {
      const input: CombinedOrganizationSettingsInput = {
        notifyWeeklyStatsEnabled: value
      };

      props
        .updateEmailNotification({
          // variables: { input: input, organizationId: selectedOrganization.id }
          variables: { input: input, organizationId: selectedOrganization.id }
          // variables: { input: input }
        })
        .then(() => {
          complete(true);
        })
        .catch(e => {
          const errorForRemapping = e as ApolloError;
          const mappedError = remapApolloErrors(errorForRemapping);
          showToast({
            summary: 'Error in updating your settings',
            detail: mappedError,
            severity: 'error'
          });
          complete(false);
        });
    } else {
      complete(false);
    }
  };

  const notifyInstagramPostEnabledHandler: ToggleAction = (value, complete) => {
    if (selectedOrganization) {
      const input: CombinedOrganizationSettingsInput = {
        notifyInstagramPostEnabled: value
      };

      props
        .updateEmailNotification({
          // variables: { input: input, organizationId: selectedOrganization.id }
          variables: { input: input, organizationId: selectedOrganization.id }
          // variables: { input: input }
        })
        .then(() => {
          complete(true);
        })
        .catch(e => {
          const errorForRemapping = e as ApolloError;
          const mappedError = remapApolloErrors(errorForRemapping);
          showToast({
            summary: 'Error in updating your settings',
            detail: mappedError,
            severity: 'error'
          });
          complete(false);
        });
    } else {
      complete(false);
    }
  };

  const notifyPostProfileErrorEnabledHandler: ToggleAction = (value, complete) => {
    if (selectedOrganization) {
      const input: CombinedOrganizationSettingsInput = {
        notifyPostProfileErrorEnabled: value
      };

      props
        .updateEmailNotification({
          // variables: { input: input, organizationId: selectedOrganization.id }
          variables: { input: input, organizationId: selectedOrganization.id }
          // variables: { input: input }
        })
        .then(() => {
          complete(true);
        })
        .catch(e => {
          const errorForRemapping = e as ApolloError;
          const mappedError = remapApolloErrors(errorForRemapping);
          showToast({
            summary: 'Error in updating your settings',
            detail: mappedError,
            severity: 'error'
          });
          complete(false);
        });
    } else {
      complete(false);
    }
  };

  const notifyAutoRssEnabledHandler: ToggleAction = (value, complete) => {
    if (selectedOrganization) {
      const input: CombinedOrganizationSettingsInput = {
        notifyAutoRssEnabled: value
      };

      props
        .updateEmailNotification({
          // variables: { input: input, organizationId: selectedOrganization.id }
          variables: { input: input, organizationId: selectedOrganization.id }
          // variables: { input: input }
        })
        .then(() => {
          complete(true);
        })
        .catch(e => {
          const errorForRemapping = e as ApolloError;
          const mappedError = remapApolloErrors(errorForRemapping);
          showToast({
            summary: 'Error in updating your settings',
            detail: mappedError,
            severity: 'error'
          });
          complete(false);
        });
    } else {
      complete(false);
    }
  };

  const notifyMailingListEnabledHandler: ToggleAction = (value, complete) => {
    if (userData) {
      const input: UserSettingsInput = {
        //userId: userData.id,
        notifyMailingList: value
      };

      props
        .updateUserSettings({
          variables: { input: input }
        })
        .then(() => {
          complete(true);
        })
        .catch(e => {
          const errorForRemapping = e as ApolloError;
          const mappedError = remapApolloErrors(errorForRemapping);
          showToast({
            summary: 'Error in updating your settings',
            detail: mappedError,
            severity: 'error'
          });
          complete(false);
        });
    } else {
      complete(false);
    }
  };

  const notifyProductUpdatesEnabledHandler: ToggleAction = (value, complete) => {
    if (userData) {
      const input: UserSettingsInput = {
        notifyProductUpdates: value
      };

      props
        .updateUserSettings({
          variables: { input: input, userId: userData.id }
        })
        .then(() => {
          complete(true);
        })
        .catch(e => {
          const errorForRemapping = e as ApolloError;
          const mappedError = remapApolloErrors(errorForRemapping);
          showToast({
            summary: 'Error in updating your settings',
            detail: mappedError,
            severity: 'error'
          });
          complete(false);
        });
    } else {
      complete(false);
    }
  };

  const notifyReferralCreditsEnabledHandler: ToggleAction = (value, complete) => {
    if (userData) {
      const input: UserSettingsInput = {
        notifyReferralCredits: value
      };

      props
        .updateUserSettings({
          variables: { input: input, userId: userData.id }
        })
        .then(() => {
          complete(true);
        })
        .catch(e => {
          const errorForRemapping = e as ApolloError;
          const mappedError = remapApolloErrors(errorForRemapping);
          showToast({
            summary: 'Error in updating your settings',
            detail: mappedError,
            severity: 'error'
          });
          complete(false);
        });
    } else {
      complete(false);
    }
  };

  const notifyFeatureAddedEnabledHandler: ToggleAction = (value, complete) => {
    if (userData) {
      const input: UserSettingsInput = {
        notifyFeatureAdded: value
      };

      props
        .updateUserSettings({
          variables: { input: input, userId: userData.id }
        })
        .then(() => {
          complete(true);
        })
        .catch(e => {
          const errorForRemapping = e as ApolloError;
          const mappedError = remapApolloErrors(errorForRemapping);
          showToast({
            summary: 'Error in updating your settings',
            detail: mappedError,
            severity: 'error'
          });
          complete(false);
        });
    } else {
      complete(false);
    }
  };

  const isLoading: boolean =
    props.getEmailNotificationSettingsResults.loading || !props.getEmailNotificationSettingsResults.called;

  const viewProps: EmailNotificationViewProps = {
    ...props,
    rules,
    toggleRules,
    formInstance,
    isLoading,
    userSettings,
    selectedOrgRole,
    notifySubscriptionEnabledHandler,
    notifyQueueEmptyEnabledHandler,
    notifyPostProfileErrorEnabledHandler,
    notifyWeeklyStatsEnabledHandler,
    notifyAutoRssEnabledHandler,
    notifyInstagramPostEnabledHandler,
    notifyMailingListEnabledHandler,
    notifyProductUpdatesEnabledHandler,
    notifyReferralCreditsEnabledHandler,
    notifyFeatureAddedEnabledHandler
  };

  return <EmailNotificationView {...viewProps} />;
};
